export type EntityType =
  | "company"
  | "person"
  | "deal"
  | "activity"
  | "note"
  | "ignore";

export interface FieldDefinition {
  id: string;
  entity: EntityType;
  label: string;
  description?: string;
  required?: boolean;
  aliases: string[];
}

export const ENTITY_CONFIG: Record<
  EntityType,
  { label: string; color: string; icon: string; shortLabel: string }
> = {
  company: {
    label: "Organização (Empresa)",
    shortLabel: "Empresa",
    color: "text-blue-600 bg-blue-500/10 border-blue-500/30",
    icon: "Building2",
  },
  person: {
    label: "Pessoa (Contato)",
    shortLabel: "Pessoa",
    color: "text-emerald-600 bg-emerald-500/10 border-emerald-500/30",
    icon: "User",
  },
  deal: {
    label: "Negócio (Oportunidade)",
    shortLabel: "Negócio",
    color: "text-amber-600 bg-amber-500/10 border-amber-500/30",
    icon: "Briefcase",
  },
  activity: {
    label: "Atividade / Tarefa",
    shortLabel: "Atividade",
    color: "text-purple-600 bg-purple-500/10 border-purple-500/30",
    icon: "Calendar",
  },
  note: {
    label: "Anotação",
    shortLabel: "Nota",
    color: "text-rose-600 bg-rose-500/10 border-rose-500/30",
    icon: "FileText",
  },
  ignore: {
    label: "Não importar esta coluna",
    shortLabel: "Ignorar",
    color: "text-muted-foreground bg-muted border-border",
    icon: "X",
  },
};

export const AVAILABLE_FIELDS: FieldDefinition[] = [
  // ─── ORGANIZAÇÃO (EMPRESA) ────────────────────────────────────────────────
  {
    id: "company.name",
    entity: "company",
    label: "Razão Social / Nome",
    required: true,
    aliases: [
      "empresa",
      "razao social",
      "razao_social",
      "nome da empresa",
      "organizacao",
      "nome fantasia",
      "cliente pj",
      "company",
      "company name",
      "company_name",
    ],
  },
  {
    id: "company.owner_name",
    entity: "company",
    label: "Responsável (Nome)",
    description: "Nome do vendedor/responsável pela empresa",
    aliases: [
      "responsavel empresa",
      "proprietario empresa",
      "vendedor empresa",
      "owner empresa",
      "company owner",
    ],
  },
  {
    id: "company.owner_email",
    entity: "company",
    label: "Responsável (E-mail)",
    description: "E-mail do vendedor/responsável para vincular ao usuário",
    aliases: [
      "email responsavel empresa",
      "email proprietario empresa",
      "email vendedor empresa",
      "owner email empresa",
    ],
  },
  {
    id: "company.tax_identifier",
    entity: "company",
    label: "CNPJ / CPF",
    aliases: [
      "cnpj",
      "cpf_cnpj",
      "cnpj_cpf",
      "documento",
      "doc",
      "tax id",
      "tax_identifier",
      "inscricao estadual",
      "ie",
    ],
  },
  {
    id: "company.sector",
    entity: "company",
    label: "Setor / Segmento",
    aliases: ["setor", "ramo", "segmento", "industria", "sector", "industry"],
  },
  {
    id: "company.size",
    entity: "company",
    label: "Porte / Nº Funcionários",
    aliases: [
      "porte",
      "tamanho",
      "num funcionarios",
      "funcionarios",
      "colaboradores",
      "size",
    ],
  },
  {
    id: "company.revenue",
    entity: "company",
    label: "Faturamento Estimado",
    aliases: [
      "faturamento",
      "receita",
      "faturamento anual",
      "faturamento mensal",
      "revenue",
    ],
  },
  {
    id: "company.phone_number",
    entity: "company",
    label: "Telefone da Empresa",
    aliases: [
      "telefone empresa",
      "tel empresa",
      "fone empresa",
      "telefone corporativo",
      "company phone",
    ],
  },
  {
    id: "company.website",
    entity: "company",
    label: "Website / Site",
    aliases: ["site", "website", "url", "pagina", "home page"],
  },
  {
    id: "company.linkedin_url",
    entity: "company",
    label: "LinkedIn da Empresa",
    aliases: ["linkedin empresa", "linkedin company", "company linkedin"],
  },
  {
    id: "company.address",
    entity: "company",
    label: "Endereço (Rua, Número)",
    aliases: ["endereco", "logradouro", "rua", "address", "street"],
  },
  {
    id: "company.city",
    entity: "company",
    label: "Cidade",
    aliases: ["cidade", "municipio", "city"],
  },
  {
    id: "company.state_abbr",
    entity: "company",
    label: "Estado (UF)",
    aliases: ["uf", "estado", "state"],
  },
  {
    id: "company.zipcode",
    entity: "company",
    label: "CEP",
    aliases: ["cep", "codigo postal", "zipcode", "zip"],
  },
  {
    id: "company.description",
    entity: "company",
    label: "Descrição da Empresa",
    aliases: ["descricao empresa", "observacao empresa", "sobre a empresa"],
  },

  // ─── PESSOA (CONTATO) ─────────────────────────────────────────────────────
  {
    id: "person.full_name",
    entity: "person",
    label: "Nome Completo",
    required: false,
    aliases: [
      "nome",
      "nome completo",
      "contato",
      "cliente",
      "pessoa",
      "cliente pf",
      "nome do contato",
      "name",
      "full name",
      "full_name",
      "contact name",
    ],
  },
  {
    id: "person.first_name",
    entity: "person",
    label: "Primeiro Nome",
    aliases: ["primeiro nome", "primeiro_nome", "first name", "first_name"],
  },
  {
    id: "person.last_name",
    entity: "person",
    label: "Sobrenome",
    aliases: ["sobrenome", "ultimo nome", "last name", "last_name"],
  },
  {
    id: "person.owner_name",
    entity: "person",
    label: "Responsável (Nome)",
    description: "Nome do vendedor/responsável pelo contato",
    aliases: [
      "responsavel contato",
      "proprietario contato",
      "vendedor contato",
      "owner contato",
      "person owner",
    ],
  },
  {
    id: "person.owner_email",
    entity: "person",
    label: "Responsável (E-mail)",
    description: "E-mail do vendedor/responsável para vincular ao usuário",
    aliases: [
      "email responsavel contato",
      "email proprietario contato",
      "email vendedor contato",
      "owner email contato",
    ],
  },
  {
    id: "person.email_work",
    entity: "person",
    label: "E-mail Comercial (Principal)",
    aliases: [
      "email",
      "e-mail",
      "email comercial",
      "e-mail comercial",
      "email trabalho",
      "e-mail trabalho",
      "pessoa email trabalho",
      "pessoa e-mail trabalho",
      "correio eletronico",
      "mail",
      "work email",
      "email 1",
      "e-mail 1",
    ],
  },
  {
    id: "person.email_home",
    entity: "person",
    label: "E-mail Pessoal",
    aliases: [
      "email pessoal",
      "e-mail pessoal",
      "email particular",
      "email residencial",
      "pessoa email pessoal",
      "personal email",
      "email 2",
      "e-mail 2",
    ],
  },
  {
    id: "person.email_other",
    entity: "person",
    label: "E-mail Adicional / Outro",
    description: "E-mail secundário ou alternativo do contato",
    aliases: [
      "outro email",
      "outro e-mail",
      "email outros",
      "e-mail outros",
      "pessoa email outros",
      "email adicional",
      "e-mail adicional",
      "email 3",
      "e-mail 3",
      "other email",
    ],
  },
  {
    id: "person.phone_work",
    entity: "person",
    label: "Telefone Comercial (Principal)",
    aliases: [
      "telefone",
      "fone",
      "tel",
      "telefone comercial",
      "telefone trabalho",
      "pessoa telefone trabalho",
      "phone",
      "work phone",
      "telefone 1",
      "tel 1",
      "fone 1",
    ],
  },
  {
    id: "person.phone_home",
    entity: "person",
    label: "Celular / WhatsApp",
    aliases: [
      "celular",
      "whatsapp",
      "whats",
      "zap",
      "mobile",
      "telefone celular",
      "pessoa telefone celular",
      "cel",
      "celular 1",
    ],
  },
  {
    id: "person.phone_other",
    entity: "person",
    label: "Telefone / Celular Adicional",
    description: "Segundo ou terceiro número de contato",
    aliases: [
      "telefone residencial",
      "pessoa telefone residencial",
      "telefone outros",
      "pessoa telefone outros",
      "telefone 2",
      "telefone 3",
      "tel 2",
      "tel 3",
      "fone 2",
      "fone 3",
      "celular 2",
      "whatsapp 2",
      "outro telefone",
      "telefone adicional",
      "telefone fixo",
      "tel fixo",
      "other phone",
    ],
  },
  {
    id: "person.phone_direct",
    entity: "person",
    label: "Telefone Direto / Ramal",
    aliases: [
      "ramal",
      "telefone direto",
      "direto",
      "extension",
      "direct phone",
    ],
  },
  {
    id: "person.title",
    entity: "person",
    label: "Cargo / Função",
    aliases: [
      "cargo",
      "funcao",
      "profissao",
      "ocupacao",
      "title",
      "job title",
      "role",
    ],
  },
  {
    id: "person.gender",
    entity: "person",
    label: "Gênero (M/F)",
    aliases: ["genero", "sexo", "gender"],
  },
  {
    id: "person.linkedin_url",
    entity: "person",
    label: "LinkedIn da Pessoa",
    aliases: [
      "linkedin",
      "linkedin pessoa",
      "perfil linkedin",
      "linkedin profile",
    ],
  },
  {
    id: "person.background",
    entity: "person",
    label: "Observações / Background",
    aliases: [
      "observacoes",
      "observacao",
      "background",
      "notas do contato",
      "detalhes contato",
    ],
  },
  {
    id: "person.tags",
    entity: "person",
    label: "Tags / Etiquetas",
    aliases: ["tags", "etiquetas", "marcadores", "categorias contato"],
  },

  // ─── NEGÓCIO / LEAD ──────────────────────────────────────────────────────
  {
    id: "deal.name",
    entity: "deal",
    label: "Título do Negócio",
    description:
      "Se omitido, será gerado como '{Pessoa} - {Empresa}' automaticamente",
    aliases: [
      "titulo",
      "titulo do negocio",
      "nome do negocio",
      "negocio",
      "oportunidade",
      "titulo oportunidade",
      "deal",
      "deal name",
      "lead",
      "titulo lead",
    ],
  },
  {
    id: "deal.contact_name",
    entity: "deal",
    label: "Contato / Pessoa (Nome)",
    description: "Nome da pessoa de contato vinculada a esta oportunidade",
    aliases: [
      "contato",
      "nome contato",
      "nome do contato",
      "pessoa contato",
      "pessoa",
      "nome pessoa",
      "deal contact",
      "contact name",
      "contato oportunidade",
      "contato negocio",
    ],
  },
  {
    id: "deal.contact_email",
    entity: "deal",
    label: "Contato (E-mail)",
    description: "E-mail de contato da pessoa vinculada ao negócio",
    aliases: [
      "email contato",
      "e-mail contato",
      "email do contato",
      "e-mail do contato",
      "contato email",
      "contact email",
    ],
  },
  {
    id: "deal.contact_phone",
    entity: "deal",
    label: "Contato (Telefone / Celular / WhatsApp)",
    description: "Telefone ou WhatsApp da pessoa vinculada ao negócio",
    aliases: [
      "telefone contato",
      "tel contato",
      "celular contato",
      "whatsapp contato",
      "whats contato",
      "contato telefone",
      "contato tel",
      "contato celular",
      "contato whatsapp",
      "contact phone",
    ],
  },
  {
    id: "deal.company_name",
    entity: "deal",
    label: "Empresa / Organização",
    description: "Nome da empresa/organização vinculada a esta oportunidade",
    aliases: [
      "empresa negocio",
      "empresa oportunidade",
      "organizacao oportunidade",
      "empresa do negocio",
      "organizacao do negocio",
      "deal company",
      "deal organization",
    ],
  },
  {
    id: "deal.owner_name",
    entity: "deal",
    label: "Responsável (Nome)",
    description: "Nome do vendedor/dono da oportunidade",
    aliases: [
      "responsavel",
      "proprietario",
      "dono",
      "owner",
      "bdr responsavel",
      "sdr responsavel",
      "closer responsavel",
      "responsavel negocio",
      "responsavel oportunidade",
      "vendedor",
      "proprietario negocio",
      "dono negocio",
      "deal owner",
      "owner negocio",
    ],
  },
  {
    id: "deal.owner_email",
    entity: "deal",
    label: "Responsável (E-mail)",
    description: "E-mail do responsável para encontrar o usuário no CRM",
    aliases: [
      "email responsavel negocio",
      "email vendedor",
      "email proprietario negocio",
      "owner email negocio",
      "deal owner email",
    ],
  },
  {
    id: "deal.amount",
    entity: "deal",
    label: "Valor (R$)",
    aliases: [
      "valor",
      "valor do negocio",
      "valor da proposta",
      "valor oportunidade",
      "honorarios",
      "mensalidade",
      "preco",
      "amount",
      "deal amount",
      "deal value",
    ],
  },
  {
    id: "deal.funil",
    entity: "deal",
    label: "Funil de Vendas",
    aliases: ["funil", "pipeline", "funil de vendas", "nome funil"],
  },
  {
    id: "deal.stage",
    entity: "deal",
    label: "Etapa / Fase",
    aliases: ["etapa", "fase", "estagio", "stage", "deal stage"],
  },
  {
    id: "deal.status",
    entity: "deal",
    label: "Status (Aberto/Ganho/Perdido)",
    aliases: [
      "status",
      "status do negocio",
      "status negocio",
      "negocio status",
      "status lead",
      "situacao",
    ],
  },
  {
    id: "deal.created_at",
    entity: "deal",
    label: "Data de Criação",
    description:
      "Data original em que o negócio foi criado no sistema anterior",
    aliases: [
      "criado em",
      "negocio criado em",
      "data de criacao",
      "data criacao",
      "data de entrada",
      "created at",
      "add time",
      "deal created",
    ],
  },
  {
    id: "deal.expected_closing_date",
    entity: "deal",
    label: "Previsão de Fechamento",
    aliases: [
      "previsao de fechamento",
      "data fechamento",
      "previsao fechamento",
      "data prevista",
      "expected close",
      "closing date",
    ],
  },
  {
    id: "deal.category",
    entity: "deal",
    label: "Categoria do Negócio",
    aliases: [
      "categoria",
      "categoria do negocio",
      "tipo de negocio",
      "deal category",
    ],
  },
  {
    id: "deal.temperatura",
    entity: "deal",
    label: "Temperatura (Quente/Morno/Frio)",
    aliases: ["temperatura", "temperatura lead", "grau de interesse"],
  },
  {
    id: "deal.description",
    entity: "deal",
    label: "Descrição / Detalhes",
    description: "Contexto, resumo ou histórico do negócio",
    aliases: [
      "descricao negocio",
      "detalhes negocio",
      "contexto negocio",
      "historico negocio",
      "deal description",
      "deal details",
      "resumo negocio",
    ],
  },
  {
    id: "deal.source",
    entity: "deal",
    label: "Origem / Fonte do Lead",
    description: "Canal que originou o lead (ex: LinkedIn, Site, Indicação)",
    aliases: [
      "origem",
      "fonte",
      "canal",
      "lead source",
      "source",
      "origem do lead",
      "como chegou",
    ],
  },
  {
    id: "deal.probability",
    entity: "deal",
    label: "Probabilidade de Fechamento (%)",
    aliases: [
      "probabilidade",
      "chance de fechamento",
      "probabilidade de fechamento",
      "probability",
      "win probability",
    ],
  },
  {
    id: "deal.lost_reason",
    entity: "deal",
    label: "Motivo de Perda",
    aliases: [
      "motivo perda",
      "razao perda",
      "lost reason",
      "motivo de perda",
      "razao de perda",
    ],
  },
  {
    id: "deal.tags",
    entity: "deal",
    label: "Tags / Etiquetas do Negócio",
    aliases: [
      "tags negocio",
      "etiquetas negocio",
      "marcadores negocio",
      "deal tags",
      "etiquetas do negocio",
      "tags do negocio",
      "etiqueta negocio",
      "tag negocio",
      "etiqueta do negocio",
      "tag do negocio",
      "etiquetas oportunidade",
      "tags oportunidade",
      "etiquetas lead",
      "tags lead",
      "etiquetas",
      "etiqueta",
      "tags",
      "tag",
      "marcadores",
      "marcador",
    ],
  },

  // ─── ATIVIDADE / TAREFA ──────────────────────────────────────────────────
  {
    id: "activity.text",
    entity: "activity",
    label: "Título da Atividade / Tarefa",
    aliases: [
      "atividade",
      "proxima atividade",
      "proxima acao",
      "tarefa",
      "proxima tarefa",
      "acao",
      "task",
    ],
  },
  {
    id: "activity.type",
    entity: "activity",
    label: "Tipo (reunião, chamada, email…)",
    aliases: ["tipo atividade", "tipo tarefa", "activity type"],
  },
  {
    id: "activity.due_date",
    entity: "activity",
    label: "Prazo / Data de Vencimento",
    aliases: [
      "data atividade",
      "prazo atividade",
      "vencimento atividade",
      "due date",
    ],
  },
  {
    id: "activity.owner_name",
    entity: "activity",
    label: "Responsável (Nome)",
    description: "Nome do usuário que ficará responsável pela atividade",
    aliases: [
      "responsavel atividade",
      "responsavel tarefa",
      "assignee",
      "owner atividade",
      "activity owner",
    ],
  },
  {
    id: "activity.owner_email",
    entity: "activity",
    label: "Responsável (E-mail)",
    description: "E-mail do usuário responsável para encontrá-lo no CRM",
    aliases: [
      "email responsavel atividade",
      "email assignee",
      "owner email atividade",
      "activity owner email",
    ],
  },

  // ─── ANOTAÇÃO ─────────────────────────────────────────────────────────────
  {
    id: "note.text",
    entity: "note",
    label: "Texto da Anotação",
    aliases: [
      "anotacao",
      "nota",
      "anotacoes",
      "historico",
      "comentario",
      "observacoes gerais",
      "note",
      "notes",
    ],
  },
];

/**
 * Campos que aceitam várias colunas da planilha (os valores são acumulados).
 */
export const MULTI_COLUMN_FIELDS = new Set([
  "person.phone_work",
  "person.phone_home",
  "person.phone_other",
  "person.phone_direct",
  "person.email_work",
  "person.email_home",
  "person.email_other",
  "deal.contact_phone",
  "deal.contact_email",
  "note.text",
  "deal.tags",
  "person.tags",
]);

/**
 * Prefixos de entidade usados em exportações (Pipedrive, RD, HubSpot…):
 * "Negócio - Título", "Pessoa - Nome", "Organização - Nome"…
 */
const ENTITY_PREFIXES: Record<string, EntityType> = {
  negocio: "deal",
  negocios: "deal",
  deal: "deal",
  deals: "deal",
  oportunidade: "deal",
  lead: "deal",
  pessoa: "person",
  pessoas: "person",
  person: "person",
  contato: "person",
  contact: "person",
  organizacao: "company",
  organizacoes: "company",
  organization: "company",
  empresa: "company",
  company: "company",
  atividade: "activity",
  activity: "activity",
  nota: "note",
  anotacao: "note",
  note: "note",
};

/**
 * Normaliza um texto para comparação (sem acentos, minúsculo, sem pontuação)
 */
export function normalizeKey(str: string): string {
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const STOP_WORDS = new Set(["de", "do", "da", "dos", "das", "e", "o", "a"]);

/** Tokeniza e singulariza ("Anúncios" → "anuncio", "Campanhas" → "campanha") */
function tokens(str: string): string[] {
  return normalizeKey(str)
    .split(" ")
    .filter((t) => t && !STOP_WORDS.has(t))
    .map((t) => (t.length > 3 && t.endsWith("s") ? t.slice(0, -1) : t));
}

/**
 * Separa o cabeçalho em: entidade (se houver prefixo) e o nome do campo,
 * removendo tags entre colchetes/parênteses como "[ADS]" ou "[PRÉ-VENDA]".
 */
function parseHeader(header: string): {
  entity: EntityType | null;
  fieldPart: string;
} {
  const parts = header
    .split(/\s+[-–—|:]\s+/)
    .map((p) => p.trim())
    .filter(Boolean);
  let entity: EntityType | null = null;
  if (parts.length > 1) {
    const first = normalizeKey(parts[0]);
    if (ENTITY_PREFIXES[first]) {
      entity = ENTITY_PREFIXES[first];
      parts.shift();
    }
  }
  const fieldPart = parts
    .join(" ")
    .replace(/\[[^\]]*\]/g, " ")
    .replace(/\([^)]*\)/g, " ")
    .trim();
  return { entity, fieldPart: fieldPart || parts.join(" ") };
}

/**
 * Calcula a pontuação de cada campo para um cabeçalho e retorna a lista
 * ordenada (melhor primeiro). Pontuação 0 = sem correspondência.
 */
export function rankFieldCandidates(
  header: string,
  fieldsList: FieldDefinition[] = AVAILABLE_FIELDS,
  preferredEntity?: string,
): Array<{ fieldId: string; score: number }> {
  const fullNorm = normalizeKey(header);
  if (!fullNorm) return [];

  const { entity, fieldPart } = parseHeader(header);
  const partNorm = normalizeKey(fieldPart);
  const partTokens = tokens(fieldPart);
  const partJoined = partTokens.join(" ");

  const results: Array<{ fieldId: string; score: number }> = [];

  for (const field of fieldsList) {
    const isCustom = field.id.startsWith("deal.cf_");
    const names = [field.label, ...field.aliases];
    let best = 0;

    for (let i = 0; i < names.length; i++) {
      const rawName = names[i];
      const isLabel = i === 0;
      const name = rawName.replace(/\([^)]*\)/g, " ");
      const aNorm = normalizeKey(name);
      if (!aNorm) continue;
      const aTokens = tokens(name);
      const aJoined = aTokens.join(" ");
      let s = 0;

      if (aNorm === fullNorm) {
        s = isLabel ? 990 : 1000;
      } else if (aNorm === partNorm || (aJoined && aJoined === partJoined)) {
        s = isLabel ? 890 : 900;
      } else if (aTokens.length > 0 && partTokens.length > 0) {
        const aInPart = aTokens.every((t) => partTokens.includes(t));
        const partInA = partTokens.every((t) => aTokens.includes(t));
        if (aInPart) {
          // Alias contido no cabeçalho: quanto mais palavras cobertas, melhor
          s = 300 + (aTokens.length / partTokens.length) * 300;
        } else if (partInA) {
          // Cabeçalho contido no alias (ex: "Segmento" ⊂ "Segmento de Atuação")
          s = 150 + (partTokens.length / aTokens.length) * 200;
        }
      }
      if (s > best) best = s;
    }

    if (best > 0) {
      // Bônus/penalidade conforme a entidade indicada no prefixo da coluna
      if (entity) {
        if (field.entity === entity) best += 150;
        else if (
          // "Negócio - Contato" pode apontar para campos de pessoa e vice-versa
          !(entity === "deal" && field.id.startsWith("deal.contact_"))
        )
          best -= 500;
      } else if (preferredEntity && field.entity === preferredEntity) {
        // Se a coluna não tem prefixo explícito de entidade, favorece a entidade principal da importação
        best += 80;
      }
      // Detalhes do Negócio (campos personalizados) são mais específicos
      if (isCustom) best += 60;
      if (best > 0) results.push({ fieldId: field.id, score: best });
    }
  }

  return results.sort((a, b) => b.score - a.score);
}

/**
 * Encontra a melhor correspondência de campo para um cabeçalho de coluna
 */
export function autoDetectField(
  header: string,
  fieldsList: FieldDefinition[] = AVAILABLE_FIELDS,
  preferredEntity?: string,
): string | null {
  const ranked = rankFieldCandidates(header, fieldsList, preferredEntity);
  return ranked.length > 0 && ranked[0].score >= 300 ? ranked[0].fieldId : null;
}

/**
 * Mapeia todas as colunas de uma vez, garantindo que campos de valor único
 * (ex: Título do Negócio) não sejam atribuídos a duas colunas. A coluna com
 * maior pontuação fica com o campo; as demais recebem a próxima opção.
 */
export function autoDetectMapping(
  headers: string[],
  fieldsList: FieldDefinition[] = AVAILABLE_FIELDS,
  preferredEntity?: string,
): Record<string, string> {
  const MIN_SCORE = 300;
  const pairs: Array<{ header: string; fieldId: string; score: number }> = [];
  for (const h of headers) {
    for (const c of rankFieldCandidates(h, fieldsList, preferredEntity)) {
      if (c.score >= MIN_SCORE) pairs.push({ header: h, ...c });
    }
  }
  pairs.sort((a, b) => b.score - a.score);

  const mapping: Record<string, string> = {};
  const usedFields = new Set<string>();
  for (const p of pairs) {
    if (mapping[p.header]) continue;
    if (usedFields.has(p.fieldId) && !MULTI_COLUMN_FIELDS.has(p.fieldId))
      continue;
    mapping[p.header] = p.fieldId;
    usedFields.add(p.fieldId);
  }
  for (const h of headers) if (!mapping[h]) mapping[h] = "ignore";
  return mapping;
}
